import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetBulkUploadStatusById } from "../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import {
  useGetBulkTransferResult,
  useSaveBulkParticipantTransfer,
} from "../../api/services/bulkTransferParticipants/bulkTransferParticipants";
import {
  BulkInsertHeader,
  InformationModal,
  ITabs,
  MuiTabPanel,
  MuiTabs,
} from "../../components";
import {
  BULK_CATEGORY_FOR_STATUS,
  MINIMUM_TWO_PARTICIPANTS,
  PAGE_IDS,
  PAGE_URLS,
  SummaryStatus,
} from "../../helpers/constants";
import { getValueOrZero } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { bulkTransferSelectedParticipantsSelector } from "../../redux/slices/program/programSelector";
import {
  setBulkTransferSelectedParticipants,
  setBulkTransferSelectedStoreParticipants,
} from "../../redux/slices/program/programSlice";
import { BulkUploadStatusResponse } from "../bulkCreateProgram/modelsAndSchema/Models";
import { BulkSummaryDetails } from "../createBulkActivity/components/steps/bulkSummaryDetails/BulkSummaryDetails";
import { CreateBulkTransferStepper } from "./components/createBulkTransferStepper/CreateBulkTransferStepper";
import { BulkTransferDetails } from "./components/steps/bulkTransferDetails/BulkTransferDetails";
import { BulkTransferProcess } from "./components/steps/bulkTransferProcess/BulkTransferProcess";
import {
  BulkATransferSearchParticipants,
  BulkTransferSearchParticipantsRef,
} from "./components/steps/bulkTransferSearchParticipants/BulkTransferSearchParticipants";
import { CreateBulkTransferWrapper } from "./CreateBulkTransfer.style";
import {
  CreateBulkTransferFormKey,
  SaveBulkTransferPayload,
  transferSteps,
  transferTypeKey,
} from "./modelsAndSchemas/Models";
import { CreateBulkTransferSchema } from "./modelsAndSchemas/ValidationSchema";
import { bulkTransferSiteCoreKeys } from "./siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.CREATE_BULK_TRANSFER;

interface CreateBulkTransferProps {
  status?: string;
  stepValue?: number;
  bulkImportId?: string;
  isFromManageBulkProcessFlow?: boolean;
}

const CreateBulkTransfer: React.FC<CreateBulkTransferProps> = ({
  status,
  stepValue,
  bulkImportId,
  isFromManageBulkProcessFlow = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchLabel, commonLabels, commonValidationMessages } = useSiteCore();
  const searchRef = useRef<BulkTransferSearchParticipantsRef>(null);
  const [currentStep, setCurrentStep] = useState(stepValue ?? 0);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [selectedTransferType, setSelectedTransferType] = useState(
    transferTypeKey.changeCountry,
  );
  const [summaryData, setSummaryData] = useState<BulkUploadStatusResponse>(
    {} as BulkUploadStatusResponse,
  );

  const [refreshCount, setRefreshCount] = useState(0);

  const filteredParticipants = useSelector(
    bulkTransferSelectedParticipantsSelector,
  );

  const {
    mutateAsync: saveBulkParticipantTransfer,
    isSuccess: isSaveBulkParticipantTransferSuccess,
    isPending: isSaveBulkParticipantTransferLoading,
    isError: isSaveBulkParticipantTransferError,
    data: saveBulkParticipantTransferData,
  } = useSaveBulkParticipantTransfer();

  const {
    isFetching: isRecordStatusLoading,
    data: recordStatus,
    isError: isRecordStatusError,
    isSuccess: isRecordStatusSuccess,
    refetchWithParams: refetchRecordStatus,
  } = useGetBulkUploadStatusById();

  const {
    isFetching: isBulkTransferResultLoading,
    data: bulkTransferResult,
    isError: isBulkTransferResultError,
    refetchWithParams: refetchBulkTransferResult,
  } = useGetBulkTransferResult();

  const defaultValues = {
    [CreateBulkTransferFormKey.bulkProcessName]: "",
  };

  const createBulkTransferMethods = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(CreateBulkTransferSchema(commonValidationMessages)),
  });

  const { getValues, trigger, setValue } = createBulkTransferMethods;

  const tabIdOne = `${pageId}-tab1`;
  const tabIdTwo = `${pageId}-tab2`;
  const tabIdThree = `${pageId}-tab3`;
  const tabs: ITabs[] = [
    {
      id: tabIdOne,
      label: "",
    },
    {
      id: tabIdTwo,
      label: "",
    },
    {
      id: tabIdThree,
      label: "",
    },
  ];

  const selectedParticipants = useMemo(
    () => filteredParticipants.filter((participant) => participant.isSelected),
    [filteredParticipants],
  );

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSaveBulkParticipantTransferLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSaveBulkParticipantTransferLoading]);

  useEffect(() => {
    if (
      isSaveBulkParticipantTransferError ||
      isRecordStatusError ||
      isBulkTransferResultError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [
    isSaveBulkParticipantTransferError,
    isRecordStatusError,
    isBulkTransferResultError,
  ]);

  useEffect(() => {
    //reset the selected participants and program template id on page load
    dispatch(setBulkTransferSelectedParticipants([]));
  }, []);

  const handleRecordStatusSuccess = (
    recordStatus: BulkUploadStatusResponse,
  ) => {
    const { bulkProcessName } = getValues();
    setSummaryData((prev) => ({
      ...prev,
      ...recordStatus,
      processName: bulkProcessName,
    }));

    if (recordStatus.status === SummaryStatus.COMPLETED) {
      setCurrentStep(transferSteps.success);
      refetchBulkTransferResult(recordStatus.bulkImportId);
    }
  };

  useEffect(() => {
    if (isRecordStatusSuccess) {
      handleRecordStatusSuccess(recordStatus);
    }
  }, [isRecordStatusSuccess, recordStatus]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isBulkTransferResultLoading || isRecordStatusLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isBulkTransferResultLoading, isRecordStatusLoading]);

  const onNextButtonClick = () => {
    if (selectedParticipants.length >= MINIMUM_TWO_PARTICIPANTS) {
      setCurrentStep((prevStep) => prevStep + 1);
      dispatch(setBulkTransferSelectedStoreParticipants(selectedParticipants));
    } else {
      setShowInformationModal(true);
    }
  };

  const handleCloseInformation = () => {
    setShowInformationModal(false);
  };

  const handleNavigateBack = () => {
    navigate(PAGE_URLS.SEARCH_PROGRAM);
  };

  const handleBackButtonClick = () => {
    if (currentStep > 0) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };
  const unProcessedParticipants =
    !isBulkTransferResultLoading &&
    !isBulkTransferResultError &&
    bulkTransferResult != null &&
    getValueOrZero(bulkTransferResult?.unProcessedParticipants) === 0;

  useEffect(() => {
    if (isSaveBulkParticipantTransferSuccess) {
      const { bulkProcessName } = getValues();
      setSummaryData({
        ...summaryData,
        bulkImportId: saveBulkParticipantTransferData?.processId,
        totalRecords: saveBulkParticipantTransferData?.totalParticipants,
        processedRecords:
          saveBulkParticipantTransferData?.processedParticipants,
        status: saveBulkParticipantTransferData?.status,
        processName: bulkProcessName,
      });
    }
  }, [isSaveBulkParticipantTransferSuccess]);

  const onConfirmClick = (data: SaveBulkTransferPayload) => {
    trigger().then((isValid) => {
      if (isValid) {
        const { bulkProcessName } = getValues();
        const payloadWithProgramIds = {
          ...data,
          programIds: selectedParticipants
            .filter((participant) => participant.isSelected)
            .map((participant) => participant.programId),
          processName: bulkProcessName,
        };
        saveBulkParticipantTransfer(payloadWithProgramIds);
      }
    });
  };

  const onAddBulkTransfer = () => {
    searchRef.current?.handleResetButton();
    setSummaryData({} as BulkUploadStatusResponse);
    setCurrentStep(0);
    setValue(CreateBulkTransferFormKey.bulkProcessName, "");
  };

  useEffect(() => {
    if (
      currentStep === transferSteps.transferDetails &&
      summaryData.bulkImportId
    ) {
      refetchRecordStatus({
        bulkImportId: summaryData.bulkImportId,
        category: BULK_CATEGORY_FOR_STATUS.bulkTransferProcess,
      });
    }
  }, [summaryData?.bulkImportId, currentStep, refreshCount]);

  const handleRefresh = () => {
    setRefreshCount((prev) => prev + 1);
  };

  // Set summary data from props (existing useEffects will trigger API calls)
  useEffect(() => {
    if (bulkImportId && status) {
      setSummaryData((prev) => ({
        ...prev,
        bulkImportId: bulkImportId,
        status: status,
      }));
    }
  }, [bulkImportId, status]);

  // Sync stepValue prop with currentStep state
  useEffect(() => {
    if (stepValue !== undefined) {
      setCurrentStep(stepValue);
    }
  }, [stepValue]);

  useEffect(() => {
    if (status === SummaryStatus.COMPLETED && bulkImportId) {
      refetchBulkTransferResult(bulkImportId);
    }
  }, [status, bulkImportId]);

  return (
    <CreateBulkTransferWrapper data-testid="create-bulk-transfer">
      <BulkInsertHeader />
      <CreateBulkTransferStepper
        currentStep={currentStep}
        transferType={selectedTransferType}
        isAllProceed={
          isSaveBulkParticipantTransferSuccess || isFromManageBulkProcessFlow
            ? unProcessedParticipants
            : false
        }
        status={summaryData?.status}
      />
      <MuiTabs
        tabs={tabs}
        value={currentStep}
        tabsTestIds={[tabIdOne, tabIdTwo, tabIdThree]}
      >
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={currentStep}
          index={0}
          isPanelWithMount
          ariaLabelledById={tabIdOne}
        >
          <BulkATransferSearchParticipants
            ref={searchRef}
            onNextButtonClick={onNextButtonClick}
            currentStep={currentStep}
            handleNavigateBack={handleNavigateBack}
            selectedTransferType={selectedTransferType}
            setSelectedTransferType={setSelectedTransferType}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={currentStep}
          index={1}
          isPanelWithMount
          ariaLabelledById={tabIdTwo}
        >
          {summaryData.status === SummaryStatus.PROCESSING ? (
            <BulkTransferProcess
              onNewBulkActivity={onAddBulkTransfer}
              onRefresh={handleRefresh}
              summaryData={summaryData}
              pageId={pageId}
              handleNavigateBack={handleNavigateBack}
            />
          ) : (
            <FormProvider {...createBulkTransferMethods}>
              <BulkTransferDetails
                pageId={pageId}
                handleBackButtonClick={handleBackButtonClick}
                handleNavigateBack={handleNavigateBack}
                onConfirmBtnClick={onConfirmClick}
                selectedTransferType={selectedTransferType}
                currentStep={currentStep}
              />
            </FormProvider>
          )}
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel3`}
          value={currentStep}
          index={2}
          isPanelWithMount
          ariaLabelledById={tabIdThree}
        >
          <BulkSummaryDetails
            currentStep={currentStep}
            summaryData={bulkTransferResult}
            onAddBulkActivity={onAddBulkTransfer}
            pageId={pageId}
            transferType={selectedTransferType}
            isFromBulkTransfer
          />
        </MuiTabPanel>
      </MuiTabs>
      <InformationModal
        showModal={showInformationModal}
        handleModalClose={handleCloseInformation}
        handlePrimaryButtonClick={handleCloseInformation}
        message={fetchLabel(
          bulkTransferSiteCoreKeys.searchParticipantsResultsKey,
          bulkTransferSiteCoreKeys.twoParticipantsRequired,
        )}
      />
    </CreateBulkTransferWrapper>
  );
};

export default CreateBulkTransfer;
